<template>
  <div class="tr-reports">
    <div class="page">
      <div class="cover-page-container">
        <img
          class="cover-page-image"
          :src="asset('/images/tr-cover-page.png')"
          alt="Trends Report Cover Page Image"
        />
        <div class="cover-page-date" :style="getWidth">{{ date }}</div>
      </div>
    </div>
    <div class="page">
      <div class="toc-container">
        <TrendsReportPDFTableOfContents :reports="reports" />
      </div>
    </div>
    <div v-for="(section, sectionIndex) in reports" :key="sectionIndex">
      <div v-for="(reports, key) in section" :key="key" :data-section="key">
        <div v-for="(report, reportIndex) in reports" :key="report.id">
          <div
            v-if="report.cwa_data"
            :id="report.report_type_slug"
            class="page"
          >
            <component
              :is="htmlComponent(report)"
              :cwaData="report.cwa_data"
              :title="report.report_type.name"
              :section="key"
              :publishDate="report.publish_date"
            />
          </div>
          <div v-else :id="report.report_type_slug" class="page">
            <component
              :key="report.imagespath"
              :is="htmlComponent(report)"
              v-bind:content="report.json_content"
              v-bind:imagespath="`${app_url}/storage/${report.imagespath}/`"
              v-bind:report="report"
              :section="key"
              :publishDate="report.publish_date"
            ></component>
          </div>
        </div>
      </div>
    </div>
    <div class="page-last">
      <div class="add-page-container">
        <img
          class="cover-page-image"
          :src="asset('/images/product/trendsreport/tr-add-page.png')"
          alt="Trends Report Add Page Image"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, ref, onMounted } from 'vue';

  import TrendsReportPDFTableOfContents from '~/components/trendsreport/pdf/TrendsReportPDFTableOfContents.vue';
  import TrGlobalAtAGlanceTemplate from '~/components/trendsreport/pdf/templates/TrGlobalAtAGlance.vue';
  import TrAtAGlanceTemplate from '~/components/trendsreport/pdf/templates/TrAtAGlanceTemplate.vue';
  import TrIndicatorTypeBTemplate from '~/components/trendsreport/pdf/templates/TrIndicatorTypeBTemplate.vue';
  import TrIndicatorTypeATemplate from '~/components/trendsreport/pdf/templates/TrIndicatorTypeBTemplate.vue';
  import TrExecutiveSummaryTemplate from '~/components/trendsreport/pdf/templates/TrExecutiveSummaryTemplate.vue';
  import TrTrends10Template from '~/components/trendsreport/pdf/templates/TrTrends10Template.vue';
  import TrLeadingIndicatorTemplate from '~/components/trendsreport/pdf/templates/TrLeadingIndicatorTemplate.vue';
  import XMLTrTemplate from '~/components/trendsreport/pdf/templates/XmlTrTemplate.vue';

  export default {
    name: 'TrendsReportPDFCollection',
    components: {
      TrGlobalAtAGlanceTemplate,
      TrAtAGlanceTemplate,
      TrIndicatorTypeBTemplate,
      TrIndicatorTypeATemplate,
      TrExecutiveSummaryTemplate,
      TrTrends10Template,
      TrLeadingIndicatorTemplate,
      TrendsReportPDFTableOfContents,
      XMLTrTemplate,
    },
    props: {
      reports: {
        Type: Object,
        required: true,
      },
      app_url: {
        type: String,
        required: false,
      },
    },
    setup(props) {
      const assetUrl = ref('');
      onMounted(() => {
        assetUrl.value = document
          .getElementById('main-page-wrapper')
          .getAttribute('data-asset-url');
      });

      const asset = (path) => {
        return `${assetUrl.value.replace(/\/$/, '')}${path}`;
      };

      const trMapping = {
        'tr-indicator-type-a': 'TrIndicatorTypeATemplate',
        'tr-indicator-type-b': 'TrIndicatorTypeATemplate',
        'tr-at-a-glance': 'TrAtAGlanceTemplate',
        'tr-trends-10': 'TrTrends10Template',
        'tr-leading-indicator': 'TrLeadingIndicatorTemplate',
        'tr-economic-outlook': '', // todo - waiting on business
        'tr-executive-summary': 'TrExecutiveSummaryTemplate',
        'tr-indicator-types': '', // todo - do we need this?
        'tr-indicator-type-ab': '', // todo - do we need this?
        'tr-global-at-a-glance': 'TrGlobalAtAGlanceTemplate',
      };

      const htmlComponent = (report) => {
        if (report.cwa_data) {
          return trMapping[report.template.slug];
        }
        return 'XMLTrTemplate';
      };

      const date = computed(() => {
        const temp = new Date();
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        const month = monthNames[temp.getMonth()];
        const year = temp.getFullYear();
        return `${month} ${year}`;
      });

      const getWidth = computed(() => {
        let length = date.value.length;
        return `width: ${length * 95}px`;
      });

      const getLastReportId = () => {
        let lastSection = props.reports[props.reports.length - 1];
        let lastSectionKey = Object.keys(lastSection)[0]; // Assuming there's only one key in the lastSection object
        let lastReportArray = lastSection[lastSectionKey];
        let lastReport = lastReportArray[lastReportArray.length - 1];
        return lastReport.id;
      };

      const lastReportId = getLastReportId();

      return {
        htmlComponent,
        date,
        asset,
        lastReportId,
        getWidth,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .tr-report {
    margin: 0 !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }
  .page {
    page-break-after: always !important;
    padding: 0 120px;
  }

  .cover-page-container {
    position: relative;
    font-family: 'Open Sans', sans-serif !important;
  }
  .cover-page-image {
    width: 100vw;
    height: 105vh;
    margin-top: -150px;
  }
  .cover-page-date {
    color: var(--itr-blue-mid);
    font-size: 140px;
    font-weight: bold;
    position: absolute;
    top: 150px;
    padding-bottom: 130px;
    border-bottom: 10px solid var(--itr-blue);
  }
  .page-last {
    text-align: center;
    height: 100vh;
    overflow: hidden;
    margin: 1rem;
    .add-page-container {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .cover-page-image {
      padding: 200px 100px 100px 100px;
      img {
        object-fit: contain;
      }
    }
  }
</style>
